// ═══════════════════════════════════════════════════════════════════════════
// SudoContext — Sudo Mode (Step-up Auth) para operaciones destructivas
// SEC-B4 (mayo 2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Provee dos cosas a la app:
//   1. Modal global de re-auth (SudoModal) que se muestra cuando el backend
//      tira `failed-precondition` con `code: 'sudo_required'`.
//   2. Hook `useSudoMode()` con función `runSudo(fn, opts)` que envuelve una
//      llamada a un callable: si tira sudo_required, abre el modal, espera a
//      que el usuario re-confirme con Google + TOTP, llama enterSudoMode, y
//      reintenta la llamada original. Devuelve el resultado o tira el error
//      si el usuario canceló.
//
// USO TÍPICO desde un componente:
//
//   import { useSudoMode } from '@core/contexts/SudoContext'
//
//   function MiBoton() {
//     const { runSudo } = useSudoMode()
//
//     async function handleClick() {
//       try {
//         const result = await runSudo(async () => {
//           const fn = httpsCallable(functions, 'activarKillSwitch')
//           return fn({ flagId: 'oauthLockdown', motivo: 'test' })
//         }, { reason: 'Activar kill switch nivel 1' })
//         // ok, fn ejecutó
//       } catch (err) {
//         // user canceló, o falló reauth, o el backend tiró otro error
//       }
//     }
//   }
//
// El "reason" se muestra al user en el modal para que sepa qué acción está
// confirmando ("Vas a activar el kill switch nivel 1").
//
// FLOW INTERNO DE runSudo
// ───────────────────────
//   1. Llama fn() (la acción del user).
//   2. Si fn() resuelve → return result. Camino feliz.
//   3. Si fn() rechaza con HttpsError.code = 'sudo_required':
//      a. Abre el modal con reason.
//      b. Espera promesa que se resuelve cuando user:
//         - Cierra modal sin confirmar → throw 'sudo_cancelled'.
//         - Confirma con reauth + enterSudoMode OK → continúa al paso c.
//      c. Reintenta fn() (ahora el backend ve sudoUntil futuro).
//      d. Si fn() rechaza otra vez con sudo_required → significa que el reauth
//         no quedó bien grabado. Mostramos error y abortamos.
//      e. Si fn() resuelve → return result.
//   4. Si fn() rechaza con cualquier otro error → throw el error tal cual.
// ═══════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { httpsCallable } from 'firebase/functions'
import {
  GoogleAuthProvider,
  reauthenticateWithPopup,
  EmailAuthProvider,
  reauthenticateWithCredential,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
} from 'firebase/auth'
import { auth, functions } from '@core/lib/firebase'
import { createLogger } from '@core/lib/logger'
import { useRecaptcha } from '@core/hooks/useRecaptcha'

const logger = createLogger('SudoMode')

const SudoContext = createContext(null)

export function useSudoMode() {
  const ctx = useContext(SudoContext)
  if (!ctx) {
    throw new Error('useSudoMode debe usarse dentro de <SudoProvider>')
  }
  return ctx
}

/**
 * Detecta si un error tirado por un httpsCallable es de tipo sudo_required.
 */
function esSudoRequired(err) {
  if (!err) return false
  // Firebase callable HttpsError trae el code original en err.details
  const details = err?.details || err?.code || err
  return details?.code === 'sudo_required' || (typeof details === 'object' && details?.code === 'sudo_required')
}

/**
 * Detecta si el error indica que también necesita reauth de cero.
 */
function esReauthRequired(err) {
  if (!err) return false
  const details = err?.details || err
  return details?.code === 'reauth_required'
}

export function SudoProvider({ children }) {
  // Estado del modal
  const [modalOpen, setModalOpen] = useState(false)
  const [modalReason, setModalReason] = useState('')
  const [modalMinutes, setModalMinutes] = useState(30)
  const [modalStatus, setModalStatus] = useState('idle') // idle | reauthing | totp | entering | done | error
  const [modalError, setModalError] = useState(null)

  // SEC-B2 — reCAPTCHA Enterprise score-based para enterSudoMode.
  const { execute: executeRecaptcha } = useRecaptcha()

  // Resolver del MFA challenge cuando Firebase pide TOTP después del reauth Google
  const mfaResolverRef = useRef(null)

  // Promesa pendiente del runSudo en curso (se resuelve cuando el user confirma o cancela)
  const pendingPromiseRef = useRef(null)

  function openModal({ reason, minutes }) {
    return new Promise((resolve, reject) => {
      pendingPromiseRef.current = { resolve, reject }
      mfaResolverRef.current = null
      setModalReason(reason || '')
      setModalMinutes(minutes || 30)
      setModalStatus('idle')
      setModalError(null)
      setModalOpen(true)
    })
  }

  function closeModalCancelled() {
    setModalOpen(false)
    const pending = pendingPromiseRef.current
    pendingPromiseRef.current = null
    if (pending) {
      const err = new Error('sudo_cancelled')
      err.code = 'sudo_cancelled'
      pending.reject(err)
    }
  }

  function closeModalSuccess(sudoData) {
    setModalOpen(false)
    const pending = pendingPromiseRef.current
    pendingPromiseRef.current = null
    if (pending) pending.resolve(sudoData)
  }

  /**
   * Step 1 — User clickeó "Confirmar identidad". Hace reauth con Google/pwd.
   * Si Firebase exige MFA challenge → guarda el resolver y pasa a step `totp`
   * para que el modal pida el código de 6 dígitos.
   * Si Firebase no exige MFA (sesión reciente o user sin MFA) → directamente
   * llama enterSudoMode.
   */
  const handleConfirmIdentity = useCallback(async ({ password = '' } = {}) => {
    const user = auth.currentUser
    if (!user) {
      setModalError('No hay usuario logueado')
      return
    }

    setModalStatus('reauthing')
    setModalError(null)

    try {
      // 1. Reauth — distinguir Google SSO vs email/password.
      const esGoogle = user.providerData?.some(p => p.providerId === 'google.com')

      try {
        if (esGoogle) {
          // Popup Google con login_hint para auto-seleccionar la cuenta del user.
          // En uso normal (no incógnito, "permanecer conectado" activo) el
          // popup pasa rápido porque Google ya recuerda al user. En incógnito
          // o sesiones expiradas, el popup pide selección de cuenta.
          // NOTA: probamos prompt='none' (silent) pero rompe en incógnito y
          // configuraciones de tracking prevention agresivo. Mejor un popup
          // visible que un flow inestable.
          const provider = new GoogleAuthProvider()
          provider.setCustomParameters({ login_hint: user.email || '' })
          await reauthenticateWithPopup(user, provider)
        } else {
          if (!password) {
            setModalError('Necesitamos tu contraseña actual')
            setModalStatus('idle')
            return
          }
          const cred = EmailAuthProvider.credential(user.email, password)
          await reauthenticateWithCredential(user, cred)
        }
      } catch (reauthErr) {
        // Firebase pide segundo factor — capturamos el resolver para que el
        // modal pida el TOTP.
        if (reauthErr?.code === 'auth/multi-factor-auth-required') {
          mfaResolverRef.current = getMultiFactorResolver(auth, reauthErr)
          logger.info('sudo.mfa_challenge_required')
          setModalStatus('totp')
          return
        }
        throw reauthErr
      }

      // 2. No hubo MFA challenge — el reauth completó. Pasamos a entrar a sudo.
      await user.getIdToken(true)
      await callEnterSudoMode()

    } catch (err) {
      logger.warn('sudo.confirm.failed', { error: err?.message, code: err?.code })
      let msg = err?.message || 'Error inesperado'
      if (err?.code === 'auth/popup-closed-by-user') {
        msg = 'Cerraste el popup antes de confirmar. Intentá de nuevo.'
      } else if (err?.code === 'auth/cancelled-popup-request') {
        msg = 'Hay otro popup abierto. Cerralo y reintentá.'
      } else if (err?.code === 'auth/wrong-password') {
        msg = 'Contraseña incorrecta'
      }
      setModalError(msg)
      setModalStatus('idle')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalMinutes])

  /**
   * Step 2 — User pegó el código TOTP de 6 dígitos. Resolvemos el MFA challenge
   * con TotpMultiFactorGenerator.assertionForSignIn y completamos el reauth.
   */
  const handleSubmitTotpCode = useCallback(async (totpCode) => {
    const resolver = mfaResolverRef.current
    if (!resolver) {
      setModalError('Sesión de verificación expirada. Cerrá el modal y reintentá.')
      setModalStatus('idle')
      return
    }
    const cleanCode = String(totpCode || '').replace(/\D/g, '')
    if (cleanCode.length !== 6) {
      setModalError('El código debe tener 6 dígitos.')
      return
    }

    setModalStatus('reauthing')
    setModalError(null)

    try {
      // Buscar el factor TOTP del usuario (asumimos el primero — si tiene
      // varios, agarra el primer TOTP).
      const totpHint = resolver.hints.find((h) => h.factorId === 'totp') || resolver.hints[0]
      if (!totpHint) {
        throw new Error('No se encontró un factor TOTP enrollado para tu cuenta.')
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, cleanCode)
      await resolver.resolveSignIn(assertion)
      mfaResolverRef.current = null

      // Refresh del ID token y entrar a sudo.
      const user = auth.currentUser
      if (user) await user.getIdToken(true)
      await callEnterSudoMode()

    } catch (err) {
      logger.warn('sudo.totp.failed', { code: err?.code, message: err?.message })
      let msg = err?.message || 'Error inesperado'
      if (err?.code === 'auth/invalid-verification-code') {
        msg = 'Código incorrecto. Probá con el código actual de tu app de autenticación.'
      }
      setModalError(msg)
      setModalStatus('totp')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalMinutes])

  /**
   * Step 3 (final) — Llamar enterSudoMode en el backend. Si OK, cerrar modal
   * con éxito.
   */
  async function callEnterSudoMode() {
    setModalStatus('entering')
    const enterFn = httpsCallable(functions, 'enterSudoMode')
    // SEC-B2 — token reCAPTCHA Enterprise (action `enter_sudo_mode`).
    const recaptchaToken = await executeRecaptcha('enter_sudo_mode')
    const enterResult = await enterFn({ minutes: modalMinutes, recaptchaToken })
    setModalStatus('done')
    setTimeout(() => closeModalSuccess(enterResult?.data || {}), 500)
  }

  /**
   * runSudo — wrapper para llamadas que pueden requerir sudo.
   *
   * @param {Function} fn — función async que ejecuta la operación (típicamente
   *   un httpsCallable wrap). Debe poder ejecutarse 2 veces idempotentemente.
   * @param {object} [opts]
   * @param {string} [opts.reason] — descripción human-readable para mostrar
   *   al user en el modal. Ej: "Activar kill switch nivel 1".
   * @param {number} [opts.minutes=30] — duración del sudo a pedir.
   */
  const runSudo = useCallback(async (fn, opts = {}) => {
    const { reason = 'Operación sensible', minutes = 30 } = opts

    try {
      // Primer intento.
      return await fn()
    } catch (err) {
      if (!esSudoRequired(err) && !esReauthRequired(err)) {
        // No es sudo_required, propagamos.
        throw err
      }

      logger.info('sudo.required', { reason, code: err?.code })

      // Abrir modal y esperar confirmación.
      await openModal({ reason, minutes })

      // Reintentar la operación.
      try {
        return await fn()
      } catch (err2) {
        if (esSudoRequired(err2) || esReauthRequired(err2)) {
          logger.warn('sudo.retry_still_required', { reason })
          const e = new Error('No pudimos confirmar tu identidad. Intentá refrescar la página y reintentar.')
          e.code = 'sudo_retry_failed'
          throw e
        }
        throw err2
      }
    }
  }, [])

  /**
   * Abre la ventana de sudo POR ADELANTADO, sin llamar a nada antes.
   *
   * Cuándo se usa esto y cuándo `runSudo`, que es la pregunta real:
   * `runSudo` llama, ve el `sudo_required`, abre el modal y **vuelve a
   * llamar** — dos invocaciones del callable. Y `withRateLimit` chequea e
   * incrementa ANTES de correr el handler, así que el intento fallido
   * igual cuesta un token del contador. En un tier fail-closed y chico eso
   * se nota: el lote de reembolsos es 10/hora con step-up FRESCO (ventana de
   * 90 s, o sea el modal se abre casi siempre), y a dos llamadas por tanda
   * quedan 5 tandas reales de las 10 que el tier declara necesitar.
   *
   * La regla, entonces: cuando el step-up es **fresco** (una ventana tan
   * corta que el modal va a abrirse igual), se pide con `pedirSudo` y se
   * llama UNA vez. Cuando es **con gracia** (una ventana de minutos, donde
   * lo normal es que ya esté abierta y no aparezca ningún modal), va
   * `runSudo`, que en ese caso cuesta una sola llamada.
   *
   * Nació con la cola de reembolsos (T9e).
   * @param {{reason?: string, minutes?: number}} opts
   * @returns {Promise<void>} rechaza con `code: 'sudo_cancelled'` si el usuario cierra el modal.
   */
  const pedirSudo = (opts = {}) => openModal({ reason: opts.reason, minutes: opts.minutes })

  const value = {
    runSudo,
    pedirSudo,
    // expuesto solo para el SudoModal — los componentes user no los necesitan
    _modalState: {
      open: modalOpen,
      reason: modalReason,
      minutes: modalMinutes,
      status: modalStatus,
      error: modalError,
    },
    _handleConfirmIdentity: handleConfirmIdentity,
    _handleSubmitTotpCode: handleSubmitTotpCode,
    _handleCancel: closeModalCancelled,
  }

  return <SudoContext.Provider value={value}>{children}</SudoContext.Provider>
}

export default SudoContext
